<template>
  <div class="rounded-md bg-white p-6 shadow-md">
    <div class="space-y-4" ref="results">
      <h2 class="mb-2 text-center text-xl font-semibold text-gray-700">Lista de comprobantes</h2>
      <div
        class="flex flex-col-reverse items-center space-y-4 space-y-reverse lg:flex-row lg:justify-between lg:space-y-0"
      >
        <keep-alive>
          <FiltrosComprobantes v-if="!isLoading && !tipoDeudaId" @change:filter="onFilterChange" />
          <div
            v-else-if="!isLoading"
            class="flex flex-col items-center space-x-1 text-center md:flex-row"
          >
            <span>Mostrando comprobantes del tipo:</span>
            <span class="font-semibold">{{ tipoDeuda.descripcion }}</span>
          </div>
        </keep-alive>
      </div>
      <transition
        enter-active-class="transition-opacity duration-300"
        leave-active-class="transition-opacity duration-300"
        enter-class="opacity-0"
        leave-to-class="opacity-0"
        mode="out-in"
      >
        <div id="resultados-deudas">
          <Cargando v-if="isLoading" key="1" />
          <Empty key="2" v-else-if="deudas.length === 0" />
          <div key="3" v-else class="space-y-3" id="lista-deudas">
            <ListaDeudas :deudas="deudas" />
            <nav class="flex items-center justify-between bg-white px-2 py-3">
              <div class="hidden sm:block">
                <p class="text-sm text-gray-700">
                  Mostrando
                  <span class="font-medium">
                    {{ offsetStart }}
                  </span>
                  a
                  <span class="font-medium">
                    {{ offsetEnd }}
                  </span>
                  de
                  <span class="font-medium">
                    {{ deudasPagination.total_record_count }}
                  </span>
                  comprobantes
                </p>
              </div>

              <div class="flex flex-1 justify-between space-x-4 sm:justify-end">
                <button
                  @click="currentPage = currentPage - 1"
                  type="button"
                  class="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition duration-150 ease-in-out focus:outline-none focus:ring focus:ring-gray-300 focus:ring-opacity-50"
                  :class="
                    hasPrevious
                      ? 'bg-white hover:bg-gray-50'
                      : 'cursor-not-allowed bg-gray-100 opacity-50'
                  "
                  :disabled="!hasPrevious"
                >
                  <SolidChevronLeftIcon class="h-5 w-5" />
                  <span>Anterior</span>
                </button>

                <button
                  @click="currentPage = currentPage + 1"
                  type="button"
                  class="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition duration-150 ease-in-out hover:bg-gray-50 focus:outline-none focus:ring focus:ring-gray-300 focus:ring-opacity-50"
                  :class="
                    hasNext
                      ? 'bg-white hover:bg-gray-50'
                      : 'cursor-not-allowed bg-gray-100 opacity-50'
                  "
                  :disabled="!hasNext"
                >
                  <span>Siguiente</span>
                  <SolidChevronRightIcon class="h-5 w-5" />
                </button>
              </div>
            </nav>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { useService } from '@colescba/app-vue'
import { useSession } from '@colescba/ui'

import ListaDeudas from '@/components/ListaDeudas'
import FiltrosComprobantes from '@/components/FiltrosComprobantes'
import Cargando from '@/components/Core/Cargando'
import Empty from '@/components/Core/Empty'

export default {
  name: 'Comprobantes',

  props: ['tipoDeudaId'],

  components: { ListaDeudas, FiltrosComprobantes, Cargando, Empty },

  mixins: [useService('portalPagos'), useSession()],

  data() {
    return {
      deudasPagination: {},
      perPage: 10,
      filtrosAvanzados: {},
      currentPage: 1,
      isLoading: true,
      scrollTargetId: 'resultados-deudas',
      tipoDeuda: null,
    }
  },

  async mounted() {
    if (this.tipoDeudaId) {
      const response = await this.portalPagos.getTiposDeuda()
      this.tipoDeuda = response.find(tipo => tipo.id.trim() == this.tipoDeudaId)
      this.filtrosAvanzados = { tipo: this.tipoDeudaId }
    }
    await this.refreshDeudas()
  },

  computed: {
    offsetStart() {
      return 1 + (this.currentPage - 1) * this.perPage
    },
    offsetEnd() {
      return this.deudas.length + this.offsetStart - 1
    },
    hasPrevious() {
      return this.currentPage > 1
    },
    hasNext() {
      return this.currentPage < this.deudasPagination?.page_total
    },
    deudas() {
      return this.deudasPagination?.records || []
    },
  },

  methods: {
    buildAdvancedFilterString() {
      const parts = ['filter_name:FN_DeudaBusquedaAvanzada']

      if (this.filtrosAvanzados?.fecha_desde)
        parts.push(`fecha_desde:${this.filtrosAvanzados.fecha_desde}`)
      if (this.filtrosAvanzados?.fecha_hasta)
        parts.push(`fecha_hasta:${this.filtrosAvanzados.fecha_hasta}`)
      if (this.filtrosAvanzados?.estado) parts.push(`estado:${this.filtrosAvanzados.estado}`)
      if (this.filtrosAvanzados?.tipo) parts.push(`tipo:${this.filtrosAvanzados.tipo}`)
      if (this.filtrosAvanzados?.nro_comprobante)
        parts.push(`nro_comprobante:${this.filtrosAvanzados.nro_comprobante}`)

      parts.push(`page:${this.currentPage}`)
      parts.push(`limit:${this.perPage}`)
      return parts.join(',')
    },
    async refreshDeudas() {
      this.isLoading = true
      this.deudasPagination = await this.portalPagos.getDeudas(
        this.user.cuitCuil.numero,
        this.buildAdvancedFilterString(),
        'id:desc',
      )
      this.isLoading = false
      this.scrollToResults()
    },
    scrollToResults() {
      this.$nextTick(() => {
        window.setTimeout(() => {
          const listEl = document.getElementById('lista-deudas')
          const el = this.scrollTargetId ? document.getElementById(this.scrollTargetId) : null
          const fallback = this.$refs.results
          const target = listEl || el || fallback
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 350)
      })
    },
    onFilterChange(filtros) {
      this.filtrosAvanzados = filtros && typeof filtros === 'object' ? filtros : {}
      this.currentPage = 1
      this.refreshDeudas()
    },
  },

  watch: {
    async currentPage() {
      await this.refreshDeudas()
    },
  },
}
</script>
